import React, { useState } from "react";

/* Shared contact form (homepage + offer pages).
   Posts the same payload as before: { name, email, company, message }. */

const LAMBDA_URL =
  "https://j3munbr5nscqxwcp42tahbandq0yezoq.lambda-url.us-east-1.on.aws/";

const styles = `
  .cf-form { display: flex; flex-direction: column; gap: 12px; }
  .cf-input, .cf-textarea {
    width: 100%; padding: 14px 16px;
    border: 1px solid var(--rule); background: var(--paper);
    font-family: var(--sans); font-size: 15px; color: var(--ink);
    outline: none; transition: border-color 0.15s;
    border-radius: 0; -webkit-appearance: none; box-sizing: border-box;
  }
  .cf-input:focus, .cf-textarea:focus { border-color: var(--accent); }
  .cf-input::placeholder, .cf-textarea::placeholder { color: var(--ink-faint); }
  .cf-textarea { resize: vertical; min-height: 120px; }
  .cf-submit {
    width: 100%; padding: 16px;
    background: var(--accent); color: white;
    font-family: var(--sans); font-size: 12px; font-weight: 500;
    letter-spacing: 0.12em; text-transform: uppercase;
    border: none; cursor: pointer; transition: opacity 0.15s;
  }
  .cf-submit:hover:not(:disabled) { opacity: 0.85; }
  .cf-submit:disabled { opacity: 0.5; cursor: not-allowed; }
  .cf-status-ok { font-size: 14px; color: #2d7a45; padding: 10px 0; }
  .cf-status-err { font-size: 14px; color: var(--accent); padding: 10px 0; }
`;

export default function ContactForm({ service, placeholder = "Describe your system / workflow" }) {
  const empty = { name: "", email: "", company: "", message: "" };
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ ok: null, msg: "" });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const validate = () => {
    if (!form.name.trim()) return "Enter your name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return "Enter a valid email.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const error = validate();
    if (error) { setStatus({ ok: false, msg: error }); return; }
    setSubmitting(true);
    const payload = service
      ? { ...form, message: `[Service: ${service}]\n${form.message}` }
      : form;
    try {
      const res = await fetch(LAMBDA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      setForm(empty);
      setStatus({ ok: true, msg: "Thank you. We'll be in touch shortly." });
    } catch {
      setStatus({ ok: false, msg: "Something went wrong. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <style>{styles}</style>
      <form onSubmit={onSubmit} className="cf-form" noValidate>
        <input className="cf-input" name="name" placeholder="Name" aria-label="Name" autoComplete="name"
          value={form.name} onChange={onChange} />
        <input className="cf-input" name="email" type="email" placeholder="Work Email" aria-label="Work email" autoComplete="email"
          value={form.email} onChange={onChange} />
        <input className="cf-input" name="company" placeholder="Company" aria-label="Company" autoComplete="organization"
          value={form.company} onChange={onChange} />
        <textarea className="cf-textarea" name="message" rows="4" aria-label="Message"
          placeholder={placeholder}
          value={form.message} onChange={onChange} />

        {status.msg && (
          <div className={status.ok ? "cf-status-ok" : "cf-status-err"} role="status" aria-live="polite">
            {status.msg}
          </div>
        )}

        <button type="submit" className="cf-submit" disabled={submitting}>
          {submitting ? "Sending..." : "Submit"}
        </button>
      </form>
    </>
  );
}
